import { Capacitor } from "@capacitor/core";

/**
 * LIVE HEART RATE OFF A CHEST STRAP, OVER BLUETOOTH LOW ENERGY (BUD-286).
 *
 * WHY THE STRAP'S BROADCAST AND NOT AN API. WHOOP's official API exposes no continuous heart
 * rate at all — only aggregated cycles, recovery, sleep and workouts, hours later. The one
 * real-time source is the strap's own BLE broadcast, which implements the STANDARD Heart Rate
 * Service (`0x180D`): the same profile Peloton, Zwift and Concept2 read. So this reads that,
 * which is also why a Polar and a Garmin work for free — nothing here knows what a WHOOP is.
 *
 * ⚠️ DISPLAY ONLY, AND THAT IS A REQUIREMENT RATHER THAN AN OVERSIGHT. Nothing this module
 * produces is written down: not to a log, not to an endpoint, not to `preferences`. BLE has no
 * backfill, so a strap that drops at minute four leaves a hole nothing can fill — and a lossy
 * display feed recorded as if it were the record silently corrupts history. The seam therefore
 * hands out a number and keeps no history of its own.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * 🚨 THE RADIO IS TOUCHED IN EXACTLY ONE PLACE, AND IT IS {@link HeartRateStrap.choose}.
 *
 * This is the whole reason this module is shaped the way it is, and it is a deliberate
 * DEPARTURE from the v1 implementation this was ported from. v1 calls the plugin's
 * `initialize()` from a mount effect, to have the stack warm before the athlete taps. On the
 * web that is harmless — `initialize()` there is a `getAvailability()` call and a browser has
 * no Bluetooth prompt, because the chooser IS the permission. On iOS it is not harmless at
 * all: `initialize()` constructs a `CBCentralManager` (verified in the plugin's own
 * `DeviceManager.swift`), and constructing one is what makes iOS show the system Bluetooth
 * prompt. Ported unchanged, the prompt would appear the instant the timer screen opened,
 * before the athlete had asked for anything.
 *
 * A PERMISSION ASKED AT THE WRONG MOMENT IS A PERMANENT NO. iOS asks once, ever. An athlete
 * who is shown an unexplained Bluetooth prompt while trying to start a workout says no, and
 * from then on the feature is dead on that handset with no route back inside the app — only
 * through Settings, which is why {@link HeartRateStrap.choose} reports that case rather than
 * failing silently.
 *
 * So the two halves are split, and the split is the native/web branch:
 *
 *   {@link HeartRateStrap.preload}  loads JAVASCRIPT. Safe on mount. On iOS it stops there,
 *                                   deliberately, and the radio stays untouched.
 *   {@link HeartRateStrap.choose}   touches the RADIO. Must come from a real tap.
 *
 * ⚠️ WHAT `preload` BUYS ON THE WEB, and why it is not just symmetry. Web Bluetooth's
 * `requestDevice()` needs the transient activation from the click, so anything slow in front
 * of it can cost the gesture. Initialising during preload — where no prompt exists — is what
 * keeps `requestDevice()` the first real await after the tap. On iOS the chooser is the
 * plugin's own screen and has no such rule, so waking the radio there costs nothing.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ NOT VERIFIED ON A PHONE, AND NOTHING HERE CAN BE. Everything below is exercised against
 * the plugin's WEB implementation, which really is Web Bluetooth — so the parser, the support
 * matrix, the connect/subscribe/drop wiring and the disconnect callback are all genuinely
 * covered (`__tests__/heart-rate.test.ts`). The CoreBluetooth path, the iOS prompt and its
 * timing, `NSBluetoothAlwaysUsageDescription` being present and correctly worded, and how a
 * real strap behaves when it walks out of range are covered by NONE of it. See
 * `docs/device-checklist.md`.
 */

/**
 * The 16-bit assigned numbers, expanded with the standard Bluetooth Base UUID
 * (`0000xxxx-0000-1000-8000-00805f9b34fb`). Written out rather than built with the plugin's
 * `numberToUUID`, so a caller that only needs to name the characteristic — the e2e fake strap,
 * for one — does not have to load the BLE stack to do it.
 */
export const HEART_RATE_SERVICE = "0000180d-0000-1000-8000-00805f9b34fb"; // 0x180D
export const HEART_RATE_MEASUREMENT = "00002a37-0000-1000-8000-00805f9b34fb"; // 0x2A37

/**
 * One Heart Rate Measurement (`0x2A37`) notification, as a number.
 *
 * The first byte is a flags field and only bit 0 matters for the reading: it selects the WIDTH
 * of the value that follows. Bit 0 clear means a `uint8` at offset 1; bit 0 set means a
 * little-endian `uint16` there instead. A strap sends `uint8` for any rate a human produces
 * and switches to `uint16` only above 255 — never in practice, but the spec allows it, so it
 * is honoured. Reading the wrong width is the entire failure mode of this function: a `uint16`
 * read as a `uint8` shows garbage, and a `uint8` read as a `uint16` reads a byte past the
 * value. The remaining flag bits (sensor contact, energy expended, RR intervals) say nothing a
 * live readout needs and are ignored.
 */
export function parseHeartRate(value: DataView): number {
  const flags = value.getUint8(0);
  const isUint16 = (flags & 0x01) === 1;
  return isUint16 ? value.getUint16(1, true) : value.getUint8(1);
}

/**
 * WHY THERE IS NO BLUETOOTH, WHEN THERE IS NONE — and it is four answers, not one.
 *
 * Three unrelated causes used to collapse into a single message in v1, and the one shown most
 * often was the one that was actively wrong. Apple requires every iOS browser to be a WebKit
 * shell, and WebKit implements no Web Bluetooth, so `navigator.bluetooth` is genuinely absent
 * in Chrome on an iPhone. Telling that athlete to "use Chrome with Bluetooth" names a remedy
 * their platform does not have, in the browser they are already standing in.
 *
 * ORDER IS THE LOGIC HERE, not a style choice.
 *
 * `isNativePlatform` is asked FIRST because the native shell INVERTS the iOS answer. Every
 * signal the browser matrix reads is still true inside the app: WKWebView exposes no
 * `navigator.bluetooth`, and the user agent still says `iPhone`. Left to the browser rules,
 * this would answer "no browser on this platform can do this" in the one place on the platform
 * that CAN — the app exists precisely because CoreBluetooth is reachable there. The capability
 * is real and invisible to all four browser signals, which is why it arrives as its own input
 * rather than being inferred.
 *
 * iOS is then tested BEFORE the origin, because it is the only cause no other change can fix:
 * an iPhone on https and an iPhone on http are equally incapable, so answering `insecure`
 * there would send someone to fix a certificate that buys them nothing.
 *
 * THE iPAD IS THE HARD ONE. Since iPadOS 13 an iPad reports a DESKTOP Safari user agent
 * (`Macintosh`, platform `MacIntel`) and is indistinguishable from a Mac by user agent alone.
 * The discriminator is touch: no Mac reports multiple touch points and every iPad reports five.
 * Erring the other way is worse than the bug it fixes — on a real Mac "switch browser" is
 * correct and actionable advice — so the touch test is an AND, never an OR, and the threshold
 * is `> 1` rather than `> 0` so a trackpad or a touch-capable external display cannot condemn
 * a desktop.
 *
 * Takes its inputs instead of reading globals, so the whole matrix is one table in a spec.
 */
export type HeartRateSupport =
  /** Web Bluetooth is here — or CoreBluetooth is, through the native shell. */
  | "supported"
  /** An iPhone or iPad IN A BROWSER. No browser on the platform can have it. */
  | "ios"
  /** The browser implements it, but not over plain http. */
  | "insecure"
  /** A browser that simply does not implement it (Firefox, Safari on a Mac). */
  | "browser";

export function detectHeartRateSupport(env: {
  hasBluetooth: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  isSecureContext: boolean;
  /**
   * Running inside the Capacitor native shell, where BLE is CoreBluetooth's job rather than
   * the browser's. False in every browser INCLUDING a PWA installed to the home screen — an
   * installed PWA is still WebKit and still cannot do Bluetooth, so it must keep answering
   * `ios`.
   */
  isNativePlatform: boolean;
}): HeartRateSupport {
  if (env.isNativePlatform) return "supported";
  if (env.hasBluetooth) return "supported";

  const iosByUserAgent = /\b(iPhone|iPad|iPod)\b/.test(env.userAgent);
  const iPadAsMac =
    (env.platform === "MacIntel" || /\bMacintosh\b/.test(env.userAgent)) && env.maxTouchPoints > 1;
  if (iosByUserAgent || iPadAsMac) return "ios";

  if (!env.isSecureContext) return "insecure";
  return "browser";
}

/**
 * Why {@link HeartRateStrap.choose} did not produce a strap.
 *
 * `blocked` is the one that is not retryable and is the reason this is a union rather than a
 * thrown error. The others all mean "tap again"; `blocked` means iOS will never show the
 * prompt again and only its Settings page can undo it, so a caller that treats it as an
 * ordinary failure ships a button that silently does nothing for the life of the install —
 * exactly the bug `camera.ts` was rewritten to stop shipping.
 */
export type StrapChoice =
  | { status: "chosen"; deviceId: string }
  /** The chooser was dismissed, or nothing was in range. Their own choice — say nothing. */
  | { status: "cancelled" }
  /** iOS Bluetooth was permanently refused. Only Settings can undo it. */
  | { status: "blocked" }
  /** Bluetooth is off, the stack failed to load, or the radio is unusable. Retryable. */
  | { status: "failed" };

export interface HeartRateStrap {
  /**
   * Which of the four answers this runtime gives.
   *
   * Reads globals only. It touches no radio, loads no plugin and prompts for nothing, so it is
   * safe to call during a render or on mount — which is what lets the readout say something
   * honest before anyone taps.
   */
  support(): HeartRateSupport;
  /**
   * Load the BLE stack WITHOUT waking the radio. Safe from a mount effect; never throws.
   *
   * 🚨 On iOS this deliberately stops after the JavaScript. See the note at the top of this
   * file: initialising here is what fires the system prompt on a screen nobody asked to pair
   * anything from.
   */
  preload(): Promise<void>;
  /**
   * Open the chooser and answer with the strap the athlete picked.
   *
   * 🚨 THE FIRST AND ONLY THING THAT TOUCHES THE RADIO, so on iOS this is where the permission
   * prompt happens. MUST be called from a real user gesture: Web Bluetooth's chooser requires
   * the transient activation from the tap.
   */
  choose(): Promise<StrapChoice>;
  /**
   * Connect to a strap this athlete has already chosen, and say when it drops.
   *
   * No chooser, so this needs no gesture — which is what makes the automatic reconnect loop
   * possible at all. Rejects when the strap is out of range, and rejects with a
   * {@link isStrapGone} error when the stored id is not a device this phone knows any more.
   */
  connect(deviceId: string, onDropped: () => void): Promise<void>;
  /** Subscribe to the measurement. `onBeat` gets beats per minute, already parsed. */
  listen(deviceId: string, onBeat: (bpm: number) => void): Promise<void>;
  /**
   * Stop notifications and disconnect. Never throws and never rejects: this runs on the way
   * out of a screen, where there is nobody left to tell and nothing useful to do about it.
   */
  release(deviceId: string): Promise<void>;
}

const isNativeRuntime = Capacitor.isNativePlatform();

type BleModule = typeof import("@capacitor-community/bluetooth-le");

/**
 * The loaded stack, or the load in flight.
 *
 * 🔑 LAZY, AND WHAT THAT DOES AND DOES NOT BUY — measured on this project rather than
 * assumed, because the obvious claim is wrong here.
 *
 * It does NOT keep the bytes off the launch path. `vite.config.ts` routes everything matching
 * `@capacitor` into one `native-vendor` chunk, which `index.html` preloads — and that pattern
 * matches `@capacitor-community/bluetooth-le` too, so the plugin ships in the chunk every cold
 * launch already fetches (it grew from 25.48 kB to 44.36 kB when this landed). A dynamic
 * import cannot undo a manual chunk rule.
 *
 * What it DOES buy is the thing this module actually needs: the plugin is not reached until
 * something calls {@link load}, so the whole BLE stack stays out of the way of a screen that
 * may never pair anything — and, far more importantly, it keeps the loading of the stack and
 * the WAKING of the radio as two separate, separately-timed steps. That separation is the
 * entire permission design at the top of this file, and it holds whatever the bundler does.
 *
 * A FAILED LOAD IS NOT CACHED. Storing a rejected promise would turn one bad moment into a
 * capability that stays broken until the app is relaunched.
 */
let stack: Promise<BleModule> | null = null;

function load(): Promise<BleModule> {
  stack ??= import("@capacitor-community/bluetooth-le").catch((error: unknown) => {
    stack = null;
    throw error;
  });
  return stack;
}

/** Has `initialize()` run? On iOS this is the same question as "has the radio been touched?". */
let radioAwake = false;

/**
 * Load the stack AND wake the radio.
 *
 * Idempotent: `initialize()` is cheap to repeat but is the prompt on iOS, and repeating a
 * prompt is not a thing to be casual about.
 */
async function wake(): Promise<BleModule> {
  const ble = await load();
  if (!radioAwake) {
    await ble.BleClient.initialize();
    radioAwake = true;
  }
  return ble;
}

function readSupport(): HeartRateSupport {
  // No `navigator` at all is not a browser refusing anything; nothing in this app runs
  // server-side, so the honest answer is "let the caller try".
  if (typeof navigator === "undefined") return "supported";
  return detectHeartRateSupport({
    isNativePlatform: isNativeRuntime,
    hasBluetooth: "bluetooth" in navigator,
    userAgent: navigator.userAgent ?? "",
    // Deprecated, and still the only thing separating an iPad from a Mac. Read defensively
    // rather than assumed present.
    platform: (navigator as Navigator & { platform?: string }).platform ?? "",
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    isSecureContext: typeof window === "undefined" ? true : window.isSecureContext,
  });
}

/**
 * A REFUSED IOS PERMISSION, TOLD APART FROM EVERY OTHER FAILURE BY ITS MESSAGE.
 *
 * The plugin rejects `initialize()` with the literal string `"BLE permission denied"` when
 * `CBCentralManager` reports `.unauthorized` — an English literal in its own
 * `DeviceManager.swift` that is never localised. This is the same trade `camera.ts` documents
 * for a cancelled picker, and it degrades in the same safe direction: if a future version
 * rewords it, a permanent refusal reads as `failed` and the athlete is offered a retry that
 * does nothing visible — which is what happens today with no detection at all. The reverse —
 * an ordinary failure reading as `blocked` — would send someone to Settings to fix a toggle
 * that is already on, and is the direction worth avoiding.
 */
/**
 * THE STORED STRAP ID IS NOT A DEVICE THIS PHONE KNOWS ANY MORE.
 *
 * A marker rather than a message to match on: this string is ours, thrown from one place in
 * this file, so unlike {@link wasRefused} — which reads a native string nobody localises — it
 * cannot drift. Exported as a predicate so the machine never has to know it is a string at all.
 */
const STRAP_GONE = "buddy:strap-gone";

/**
 * Raise it. Exported so a test can produce the same error the seam does rather than hard-coding
 * the marker beside a second copy of it — the failure mode this constant exists to avoid.
 */
export function strapGoneError(): Error {
  return new Error(STRAP_GONE);
}

export function isStrapGone(error: unknown): boolean {
  return error instanceof Error && error.message === STRAP_GONE;
}

function wasRefused(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /permission denied|unauthoriz/i.test(message);
}

export const heartRateStrap: HeartRateStrap = {
  support: readSupport,

  async preload() {
    try {
      await load();
      /**
       * 🚨 THE NATIVE PATH STOPS HERE, and this line is the whole point of the module. The
       * next statement would construct a `CBCentralManager` and put a system prompt on screen
       * on a timer nobody has tapped anything on yet.
       */
      if (isNativeRuntime) return;

      // The web path has no prompt to be early for, and initialising now is what keeps the
      // chooser the first real await after the tap. Asking a browser that has no Bluetooth
      // would only throw, and `support()` has already answered for that case.
      if (readSupport() !== "supported") return;
      await wake();
    } catch {
      // A stack that would not load is reported on the tap, where there is somebody to tell.
    }
  },

  async choose() {
    if (readSupport() !== "supported") return { status: "failed" };

    let ble: BleModule;
    try {
      // 🚨 iOS SHOWS THE PERMISSION PROMPT INSIDE THIS CALL.
      ble = await wake();
    } catch (error) {
      return wasRefused(error) ? { status: "blocked" } : { status: "failed" };
    }

    try {
      const device = await ble.BleClient.requestDevice({
        // Filtered to the Heart Rate Service, and listed as optional as well so the granted
        // device actually exposes it for notifications on the web path. A strap already paired
        // to another receiver will not appear — that is the strap's constraint, not ours.
        services: [HEART_RATE_SERVICE],
        optionalServices: [HEART_RATE_SERVICE],
      });
      return { status: "chosen", deviceId: device.deviceId };
    } catch (error) {
      /**
       * A dismissed chooser and an empty room reject identically, and both are ordinary: the
       * athlete taps again. The one case worth separating is a refusal that arrived here
       * rather than above — on iOS the FIRST prompt can be answered "Don't Allow" while this
       * call is already in flight.
       */
      return wasRefused(error) ? { status: "blocked" } : { status: "cancelled" };
    }
  },

  async connect(deviceId, onDropped) {
    const ble = await wake();

    /**
     * ═══ 🚨 THE PLUGIN HAS TO BE REMINDED THE DEVICE EXISTS, EVERY LAUNCH ═══
     *
     * Without this line every reconnect after an app restart failed, with the plugin's own
     * words: `Device not found. Call 'requestDevice', 'requestLEScan' or 'getDevices' first.`
     *
     * The plugin keeps its known peripherals in a map that lives for the life of the PROCESS,
     * populated by `requestDevice` (the chooser), a scan, or this call. A device id we saved to
     * preferences during a previous run is just a string it has never seen, so `connect` on it
     * rejects immediately — every time, on every platform. The automatic reconnect never once
     * worked from cold; it only ever looked like it did in a session where the chooser had
     * already run and filled the map.
     *
     * `getDevices` is the documented way back: `retrievePeripherals` on iOS, which is exactly
     * "give me the peripheral for this UUID I saved earlier". The plugin's own doc names our
     * case — "device IDs, e.g. saved from a previous app run".
     *
     * ⚠️ AN EMPTY ANSWER IS A DIFFERENT FAILURE FROM A FAILED CONNECT, and they must not be
     * treated alike. Out of range is temporary and worth retrying for as long as the athlete is
     * in the gym. An id the system cannot resolve AT ALL is permanent: the app was reinstalled
     * (iOS scopes peripheral UUIDs to the installation), or the strap was unpaired. Retrying
     * that for two and a half minutes and then sitting on "Trying again" is what this reported
     * as. It is raised as its own error so the machine can forget the strap and offer a chooser.
     */
    const known = await ble.BleClient.getDevices([deviceId]).catch(() => []);
    if (known.length === 0) throw strapGoneError();

    await ble.BleClient.connect(deviceId, () => onDropped());
  },

  async listen(deviceId, onBeat) {
    const ble = await wake();
    await ble.BleClient.startNotifications(
      deviceId,
      HEART_RATE_SERVICE,
      HEART_RATE_MEASUREMENT,
      /**
       * 🔑 THE `DataView` STOPS HERE. Parsing inside the seam is what keeps a GATT wire format
       * out of the feature: the timer deals in beats per minute, and the day an Android path
       * or a second characteristic arrives, nothing above this line learns about it.
       */
      (value) => onBeat(parseHeartRate(value)),
    );
  },

  async release(deviceId) {
    // `load` rather than `wake`: if the radio was never woken there is nothing holding a
    // strap, and waking one to tear it down would be absurd — on iOS it would also be a
    // permission prompt raised by leaving a screen.
    const ble = await load().catch(() => null);
    if (!ble || !radioAwake) return;
    await ble.BleClient.stopNotifications(
      deviceId,
      HEART_RATE_SERVICE,
      HEART_RATE_MEASUREMENT,
    ).catch(() => {});
    await ble.BleClient.disconnect(deviceId).catch(() => {});
  },
};
